import { useState, useCallback } from 'react';

import { Modal } from '@simplywallst/ui-core';

import { useCompanyBatchSearch, useUploadTransactions } from '../../hooks';

import { DefaultState } from './DefaultState';
import { SuccessState } from './SuccessState';
import { ErrorState } from './ErrorState';

import { populateTransactionsWithMatchedCompanies } from '../../utils';
import { useTracking } from 'react-tracking';

type Props = {
  populatedRowCount: number;
  incompleteRowCount: number;
  onClose: () => void;
  onSuccess: (
    response: ReturnType<typeof populateTransactionsWithMatchedCompanies>
  ) => void;
};

export const CSVImportModal = ({
  populatedRowCount,
  incompleteRowCount,
  onClose,
  onSuccess,
}: Props) => {
  const { trackEvent } = useTracking();

  const [selectedFileName, setSelectedFileName] = useState<string>('');
  const { searchCompanies } = useCompanyBatchSearch();
  const [matchingStatus, setMatchingStatus] = useState<'pending' | 'completed'>(
    'pending'
  );
  const { mutate, status, reset } = useUploadTransactions({
    onSuccess: async (data) => {
      if (!data) return;

      const tickerList = data.reduce<string[]>((uniques, transaction) => {
        if (transaction.ticker !== null) {
          if (!uniques.includes(transaction.ticker)) {
            uniques.push(transaction.ticker);
          }
        }
        return uniques;
      }, []);

      // TODO: If this fails, we shouldn't really be showing success.
      // Should this request be it own useQuery that enables on success of the upload?
      const matchedCompanies = await searchCompanies(tickerList);

      const populatedTransactions = populateTransactionsWithMatchedCompanies(
        data,
        matchedCompanies
      );

      setMatchingStatus('completed');

      onSuccess(populatedTransactions);

      trackEvent({
        modifier: 'uploadTransactionsModal',
        action: 'transactionsProcessed',
      });
    },
  });

  const handleImport = useCallback(
    (file: File) => {
      setSelectedFileName(file.name);
      mutate(file);
    },
    [mutate]
  );

  const handleReset = useCallback(() => {
    setSelectedFileName('');

    trackEvent({
      modifier: 'uploadTransactionsModal',
      action: 'reset',
    });

    reset();
  }, [reset, trackEvent]);

  const handleClose = useCallback(() => {
    setSelectedFileName('');

    trackEvent({
      modifier: 'uploadTransactionsModal',
      action: 'close',
    });

    onClose();
  }, [onClose, trackEvent]);

  return (
    <Modal isNaked fullscreen>
      <div
        className="grid grid-cols-[auto] content-center md:grid-cols-[700px]"
        data-theme="light"
      >
        {status === 'error' ? (
          <ErrorState
            selectedFileName={selectedFileName}
            onClose={handleClose}
            onReset={handleReset}
          />
        ) : status === 'success' ? (
          <SuccessState
            selectedFileName={selectedFileName}
            populatedRowCount={populatedRowCount}
            incompleteRowCount={incompleteRowCount}
            matchingStatus={matchingStatus}
            onClose={handleClose}
          />
        ) : (
          <DefaultState
            onImport={handleImport}
            onClose={handleClose}
            uploadStatus={status}
            selectedFileName={selectedFileName}
            populatedRowCount={populatedRowCount}
          />
        )}
      </div>
    </Modal>
  );
};
